import { ArchitectureParseError } from "../errors";
import type { ApiEndpoint, HttpVerb, RiskSeverity } from "../types";
import { asString, asStringArray } from "../v3/parse-bullets-v3";

// Coerce js-yaml output into structured ApiEndpoint[]. New in archviz/v0.4.

const HTTP_VERB_VALUES: ReadonlySet<HttpVerb> = new Set([
  "GET",
  "POST",
  "PUT",
  "PATCH",
  "DELETE",
  "HEAD",
  "OPTIONS",
]);

const SEVERITY_VALUES: ReadonlySet<RiskSeverity> = new Set(["info", "warning", "critical"]);

interface ParseContext {
  scope: string;
  strict: boolean;
}

export function parseEndpoints(value: unknown, ctx: ParseContext): ApiEndpoint[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) return [];
  const out: ApiEndpoint[] = [];
  for (let i = 0; i < value.length; i += 1) {
    const endpoint = coerceEndpoint(value[i], { ...ctx, scope: `${ctx.scope}.endpoints[${i}]` });
    if (endpoint) out.push(endpoint);
  }
  return out;
}

function coerceEndpoint(value: unknown, ctx: ParseContext): ApiEndpoint | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope}: expected an endpoint object (id, verb, url_pattern, called_by, what, why, blast_radius).`,
      );
    }
    return null;
  }
  const rec = value as Record<string, unknown>;
  const id = asString(rec.id) ?? "";
  if (id === "") {
    if (ctx.strict) {
      throw new ArchitectureParseError(`${ctx.scope}: \`id\` is required and must be non-empty.`);
    }
    return null;
  }
  const rawVerb = (asString(rec.verb) ?? "").toUpperCase();
  if (!HTTP_VERB_VALUES.has(rawVerb as HttpVerb)) {
    if (ctx.strict) {
      throw new ArchitectureParseError(
        `${ctx.scope} (\`${id}\`): \`verb\` must be one of ${[...HTTP_VERB_VALUES].join(", ")}; got "${rawVerb}".`,
      );
    }
    return null;
  }
  const verb = rawVerb as HttpVerb;
  const url_pattern = asString(rec.url_pattern) ?? "";
  const what = asString(rec.what) ?? "";
  const why = asString(rec.why) ?? "";
  const blast_radius = asString(rec.blast_radius) ?? "";
  const called_by = asStringArray(rec.called_by);
  if (ctx.strict) {
    if (url_pattern === "") {
      throw new ArchitectureParseError(`${ctx.scope} (\`${id}\`): \`url_pattern\` is required.`);
    }
    if (called_by.length === 0) {
      throw new ArchitectureParseError(
        `${ctx.scope} (\`${id}\`): \`called_by\` is required and must list at least one container or subsystem.`,
      );
    }
    if (what === "" || why === "" || blast_radius === "") {
      throw new ArchitectureParseError(
        `${ctx.scope} (\`${id}\`): \`what\`, \`why\`, and \`blast_radius\` are all required and must be non-empty.`,
      );
    }
  }
  const rawSeverity = asString(rec.severity);
  const severity: RiskSeverity | undefined = rawSeverity
    ? SEVERITY_VALUES.has(rawSeverity.toLowerCase() as RiskSeverity)
      ? (rawSeverity.toLowerCase() as RiskSeverity)
      : "info"
    : undefined;
  const permission = asString(rec.permission);
  return {
    id,
    verb,
    url_pattern,
    called_by,
    what,
    why,
    blast_radius,
    severity,
    permission,
  };
}
